import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import UsersTable from './UsersTable';
import UsersTableMobile from './UsersTableMobile';
import EditUserModal from './EditUserModal';
import CreateUserModal from './CreateUserModal';
import FilterUsersModal from './FilterUsersModal';
import Alert from '../shared/Alert';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';
import { withFirebase } from '../../firebase';

import { disableUser, updateUser, fetchUsers, disable2FA, fetchUserByEmail } from '../../actions/adminActions';
import { toggleSidebar } from '../../actions/sidebarActions';
import { setCurrentUser } from '../../actions/userActions';

import { api } from '../../util';
import { getComparator, stableSort } from '../../util/table';

import urls from '../../constants/urls';
import { routes } from '../../constants';
import { getUserCompanies, getUserCountries, getUserDistributorIds } from '../../util/user';

import './users.scss';

const DUPLICATE_USER_ERROR = 'A user with this email already exists.';

const UsersTableContainer = (props) => {
  const {
    users,
    distributors,
    countries,
    manufacturers,
    radiology,
    companies,
    logistics,
    disableUser,
    disable2FA,
    fetchUsers,
    withButton,
    toggleSidebar,
    teams,
    currentUser,
    firebase,
    setUser,
  } = props;

  const theme = useTheme();
  const history = useHistory();

  const [search, setSearch] = useState('');

  const [showAlert, toggleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [showModal, toggleModal] = useState(false);
  const [showEditModal, toggleEditModal] = useState(false);
  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showDisableModal, toggleDisableModal] = useState(false);
  const [showDeactivateModal, toggleDeactivateModal] = useState(false);
  const [showFilterModal, toggleFilterModal] = useState(false);
  const [showGhostModeModal, toggleGhostModeModal] = useState(false);

  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);

  const [filterState, setFilterState] = useState({
    companies: [],
    countries: [],
    distributors: [],
    roles: [],
    statuses: [],
    lastSignInTime: null,
  });

  const onVerifyClick = (uid) => {
    setSelectedUid(uid);
    toggleModal(true);
  };

  const onClose = () => {
    toggleModal(false);
    setSelectedUid(null);
  };

  const verifyAccount = async () => {
    try {
      setLoading(true);
      await api.post(urls.verifyEmail, { uid: selectedUid });
      toggleModal(false);
      toggleAlert(true);
      setSelectedUid(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const sendSMS = (uid) => {};

  const onEditClick = (user) => {
    setErrorMessage('');
    setSelectedUser(user);
    toggleEditModal(true);
  };

  const onEditClose = () => {
    toggleEditModal(false);
    setSelectedUser(null);
  };

  const onUserCreate = async (formObj) => {
    try {
      setErrorMessage('');
      setLoading(true);

      const normalizedEmail = formObj.email?.trim();
      const existingUser = await fetchUserByEmail(normalizedEmail);

      if (existingUser) {
        throw new Error(DUPLICATE_USER_ERROR);
      }

      await api.post(urls.createUser, { ...formObj, email: normalizedEmail });
      toggleCreateModal(false);
      await refreshUsers();
      setSuccessMessage(true);
    } catch (err) {
      setErrorMessage(err?.message || 'Unable to create user.');
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const onUserEdit = async (formObj) => {
    try {
      setLoading(true);
      await api.post(urls.updateUser, { uid: selectedUser.uid, ...formObj });
      toggleEditModal(false);
      await refreshUsers();
      setSelectedUser(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onDisableClick = async (uid, value) => {
    if (!value) {
      try {
        setFetching(true)
        await disableUser(uid, false);
        await refreshUsers();
      } catch (err) {
        console.error(err);
      } finally {
        setFetching(false);
      }
    } else {
      setSelectedUid(uid);
      toggleDeactivateModal(true);
    }
  };

  const onDisableUser = async () => {
    if (selectedUid) {
      try {
        setLoading(true);
        await disableUser(selectedUid, true);
        await refreshUsers();
        toggleDeactivateModal(false);
        setSelectedUid(null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDisable2FAClick = (user) => {
    setSelectedUser(user);
    toggleDisableModal(true);
  };

  const onGhostModeClick = (user) => {
    setErrorMessage('');
    setSelectedUser(user);
    toggleGhostModeModal(true);
  };

  const onDisable2FA = async () => {
    if (selectedUser) {
      try {
        setLoading(true);
        await disable2FA(selectedUser.uid);
        setSelectedUser(null);
        toggleDisableModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onStartGhostMode = async () => {
    if (selectedUser) {
      try {
        setLoading(true);
        setErrorMessage('');
        const user = await firebase.startGhostMode(selectedUser);
        setUser(user);
        toggleGhostModeModal(false);
        setSelectedUser(null);
        history.push(routes.DASHBOARD);
      } catch (err) {
        console.error(err);
        setErrorMessage(err?.message || 'Unable to enter ghost mode.');
      } finally {
        setLoading(false);
      }
    }
  };

  const refreshUsers = async () => {
    try {
      setFetching(true);
      await fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      companies: filters.companies || [],
      distributors: filters.distributors || [],
      roles: filters.roles || [],
      countries: filters.countries || [],
      statuses: filters.statuses || [],
      lastSignInTime: filters.lastSignInTime || null,
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    if (key === 'lastSignInTime') {
      setFilterState({
        ...filterState,
        lastSignInTime: null,
      });
      return;
    }

    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    });
  };

  const filteredUsers = useMemo(() => {
    let filtered = users.map((user) => ({
      ...user,
      companies: getUserCompanies(user, distributors),
      countries: getUserCountries(user, distributors),
      distributorIds: getUserDistributorIds(user),
      lastSignInTime: user?.lastSignInTime ? moment(user.lastSignInTime).valueOf() : null,
    }));

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.firstName?.toLowerCase().includes(q) ||
          u.lastName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q)
      );
    }

    if (filterState?.companies?.length) {
      filtered = filtered?.filter((u) => u.companies?.some((company) => filterState.companies?.includes(company)));
    }
    if (filterState?.roles?.length) {
      filtered = filtered?.filter((u) => filterState?.roles?.includes(u.role));
    }
    if (filterState?.countries?.length) {
      filtered = filtered?.filter((u) => u.countries?.some((country) => filterState?.countries?.includes(country)));
    }
    if (filterState?.distributors?.length) {
      filtered = filtered?.filter((u) => u.distributorIds?.some((distributorId) => filterState?.distributors?.includes(distributorId)));
    }
    if (filterState?.statuses?.length === 1) {
      filtered = filtered?.filter((u) => filterState?.statuses[0]?.value === 'disabled' ? u.disabled : !u.disabled);
    }
    if (filterState?.lastSignInTime?.from || filterState?.lastSignInTime?.to) {
      filtered = filtered?.filter((u) => {
        if (!u?.lastSignInTime) {
          return false;
        }

        const lastSignInTime = moment(u.lastSignInTime);
        if (!lastSignInTime.isValid()) {
          return false;
        }

        const from = filterState?.lastSignInTime?.from ? moment(filterState.lastSignInTime.from).startOf('day') : null;
        const to = filterState?.lastSignInTime?.to ? moment(filterState.lastSignInTime.to).endOf('day') : null;

        if (from && lastSignInTime.isBefore(from)) {
          return false;
        }

        if (to && lastSignInTime.isAfter(to)) {
          return false;
        }

        return true;
      });
    }

    return filtered;
  }, [users, distributors, search, filterState])

  const list = useMemo(() => {
    let filtered = filteredUsers;

    if (orderBy) {
      filtered = stableSort(
        filtered,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return filtered
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredUsers, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const ghostTargetLabel = [selectedUser?.firstName, selectedUser?.lastName]
    .filter(Boolean)
    .join(' ') || selectedUser?.email || 'this user';

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Users
                  <Box className="badge">{users.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage users and their account permissions here
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <MuiIconButton
              edge="start"
              color="primary"
              aria-label="add"
              onClick={refreshUsers}
              sx={{ ml: 0, mr: '12px', display: { xs: 'none', sm: 'flex' } }}
            >
              <RefreshIcon />
            </MuiIconButton>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add User'
              onClick={() => {
                setErrorMessage('');
                toggleCreateModal(true);
              }}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {users.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <UsersTable
                users={list}
                distributors={distributors}
                onVerifyClick={onVerifyClick}
                onSMSClick={sendSMS}
                onEditClick={onEditClick}
                onDisableClick={onDisableClick}
                onDisable2FAClick={onDisable2FAClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredUsers?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                companies={companies}
                userRole={currentUser.role}
                currentUserId={currentUser?.uid}
                isGhostMode={!!currentUser?.ghostMode}
                canUseGhostMode={!!currentUser?.ghostModeEnabled}
                filter={filterState}
                onRemoveFilterItem={onRemoveFilterItem}
                onGhostModeClick={onGhostModeClick}
              />
            </Box>

              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <UsersTableMobile users={list} />
              </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Users</Box>
        )}
      </Box>

      <Alert
        variant="success"
        message="Account was successfully verified"
        open={showAlert}
        onClose={() => toggleAlert(false)}
      />

      <ConfirmationModal
        open={showModal}
        onClose={onClose}
        onSubmit={verifyAccount}
        title="Verify User"
        text="You are about to automatically verify this user and therefore grant access to the system"
        closeText="Cancel"
        submitText="Verify"
        variant="success"
      />

      <ConfirmationModal
        open={showDisableModal}
        onClose={() => toggleDisableModal(false)}
        onSubmit={onDisable2FA}
        title="Permanently Remove 2FA?"
        text="Are you sure you want to remove the 2FA enabled for this user? This action cannot be reversed."
        closeText="Cancel"
        submitText="Remove"
      />

      <ConfirmationModal
        open={showGhostModeModal}
        onClose={() => {
          toggleGhostModeModal(false);
          setSelectedUser(null);
        }}
        onSubmit={onStartGhostMode}
        title="Enter Ghost Mode?"
        text={`You are about to log in as ${ghostTargetLabel}${selectedUser?.email && ghostTargetLabel !== selectedUser.email ? ` (${selectedUser.email})` : ''}. You can exit ghost mode at any time from the app banner.`}
        closeText="Cancel"
        submitText="Login as User"
        loading={loading}
      />

      <ConfirmationModal
        open={showDeactivateModal}
        onClose={() => toggleDeactivateModal(false)}
        onSubmit={onDisableUser}
        title="Disable User"
        text="Are you sure you want to disable this user?"
        cancelText="Cancel"
        submitText="Disable"
        loading={loading}
        variant="error"
      />

      <CreateUserModal
        open={showCreateModal}
        onClose={() => {
          setErrorMessage('');
          toggleCreateModal(false);
        }}
        onSubmit={onUserCreate}
        distributorsList={distributors}
        countriesList={countries}
        manufacturers={manufacturers}
        radiologyTeams={radiology}
        loading={loading}
        companies={companies}
        logisticsTeams={logistics}
        currentUser={currentUser}
      />

      <EditUserModal
        open={showEditModal}
        onClose={onEditClose}
        onSubmit={onUserEdit}
        user={selectedUser}
        loading={loading}
        distributorsList={distributors}
        countriesList={countries}
        manufacturers={manufacturers}
        radiologyTeams={radiology}
        companies={companies}
        logisticsTeams={logistics}
        teams={teams}
        currentUser={currentUser}
      />

      <FilterUsersModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        distributors={distributors}
        countries={countries}
        companies={companies}
      />

      <Alert
        variant="success"
        message="New user successfully created"
        open={successMessage}
        onClose={() => setSuccessMessage(false)}
      />

      <Alert
        variant="error"
        message={errorMessage}
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
      />
    </>
  );
};

UsersTableContainer.propTypes = {
  users: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    currentUser: state.user.currentUser,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: (uid, userData) => dispatch(updateUser(uid, userData)),
    disableUser: (uid, value) => dispatch(disableUser(uid, value)),
    disable2FA: (uid) => dispatch(disable2FA(uid)),
    fetchUsers: () => dispatch(fetchUsers()),
    toggleSidebar: () => dispatch(toggleSidebar()),
    setUser: (user) => dispatch(setCurrentUser(user)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withFirebase(UsersTableContainer));
